/**
 * Intro request API contract.
 * 
 * ACT 7: Frontend API for sponsor intro requests.
 * Wired to the real audited backend endpoint.
 * 
 * Safety doctrine:
 * - No direct contact details collected
 * - No legal name, phone, email, address
 * - No employer, income, net worth
 * - No social media handles
 * - sponsor_id is NEVER sent to the API (server-derived from session)
 */

import type { FingerprintSignals } from './auth';

/**
 * Intro request draft - data sponsor submits.
 */
export interface IntroRequestDraft {
  /** Card ID being requested */
  cardId: string;
  /** Optional short message, max 280 chars */
  message?: string;
  /** Sponsor acknowledged safety terms */
  acknowledged: boolean;
  /** Optional fingerprint context for fraud-resistant audit. */
  /** Maps to backend fingerprint.CaptureInput. */
  fingerprint?: FingerprintSignals;
}

/**
 * Intro request status.
 */
export type IntroRequestStatus = 'pending' | 'reviewing' | 'accepted' | 'declined';

/**
 * Intro request response - confirmation of submission.
 */
export interface IntroRequestResponse {
  /** Unique request ID */
  requestId: string;
  /** Current status */
  status: IntroRequestStatus;
  /** Timestamp of submission */
  submittedAt: string;
  /** Whether this was a duplicate request */
  isDuplicate: boolean;
}

/**
 * Local state keys for intro request persistence.
 * Uses sessionStorage for ephemeral state.
 */
export const INTRO_REQUEST_KEYS = {
  SUBMITTED: 'indeep_intro_submitted',
  SUBMITTED_AT: 'indeep_intro_submitted_at',
} as const;

/**
 * Message max length per safety doctrine.
 */
export const MESSAGE_MAX_LENGTH = 280;

/**
 * API base URL for intro requests.
 */
const API_BASE = '/api/v1';

/**
 * Submit intro request to the audited backend.
 * 
 * POST /api/v1/intro-requests
 * 
 * Security notes:
 * - sponsor_id is NOT sent; backend derives it from authenticated session
 * - Only card_id, message, and acknowledged fields are sent
 * - Backend validates and emits audit events
 * 
 * Message limit: 280 Unicode characters (per backend MaxMessageLength)
 */
export async function submitIntroRequest(
  draft: IntroRequestDraft
): Promise<IntroRequestResponse> {
  // Build request body explicitly - message is omitted if not provided
  const body: Record<string, unknown> = {
    card_id: draft.cardId,
    acknowledged: draft.acknowledged,
  };
  
  // Only include message if provided
  if (draft.message) {
    body.message = draft.message;
  }
  
  // Include fingerprint if available for fraud-resistant audit context.
  // Backend handles missing fingerprint gracefully with degraded audit context.
  if (draft.fingerprint) {
    body.fingerprint = draft.fingerprint;
  }

  const response = await fetch(`${API_BASE}/intro-requests`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    // NOTE: sponsor_id is intentionally NOT included
    // Backend derives account attribution from authenticated session
    body: JSON.stringify(body),
    credentials: 'include', // Send cookies for session authentication
  });

  if (!response.ok) {
    // Parse error response
    let errorMessage = 'Failed to submit request. Please try again.';
    try {
      const errorData = await response.json();
      if (errorData.error) {
        // For 5xx errors, use generic message to avoid exposing internals
        if (response.status >= 500) {
          errorMessage = 'Failed to submit request. Please try again.';
        } else {
          errorMessage = errorData.error;
        }
      }
    } catch {
      // Use default error message if JSON parsing fails
    }
    
    throw new Error(errorMessage);
  }

  const data = await response.json();
  
  // Store locally for duplicate detection
  sessionStorage.setItem(INTRO_REQUEST_KEYS.SUBMITTED, draft.cardId);
  sessionStorage.setItem(INTRO_REQUEST_KEYS.SUBMITTED_AT, new Date().toISOString());

  return {
    requestId: data.request_id,
    status: data.status || 'pending',
    // Use backend timestamp if available, otherwise fallback to client time
    submittedAt: data.submitted_at || new Date().toISOString(),
    isDuplicate: false,
  };
}

/**
 * Check if intro request already exists for this card.
 * Calls real backend API to check server-side existence.
 * 
 * GET /api/v1/intro-requests/exists?card_id={cardId}
 * 
 * Security notes:
 * - sponsor_id is NOT sent; backend derives it from authenticated session
 * - Uses sessionStorage fallback for offline/client-side caching
 */
export async function checkIntroRequestExists(
  cardId: string
): Promise<IntroRequestResponse | null> {
  try {
    const response = await fetch(
      `${API_BASE}/intro-requests/exists?card_id=${encodeURIComponent(cardId)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }
    );

    if (!response.ok) {
      // If API fails, fall back to session storage check
      return checkSessionStorage(cardId);
    }

    const data = await response.json();
    
    if (data.exists) {
      return {
        requestId: data.request_id || `req_${cardId}`,
        status: data.status || 'pending',
        submittedAt: new Date().toISOString(),
        isDuplicate: true,
      };
    }

    return null;
  } catch {
    // Network error or API unavailable - fall back to session storage
    return checkSessionStorage(cardId);
  }
}

/**
 * Fallback check using sessionStorage.
 * Used when backend API is unavailable.
 */
function checkSessionStorage(cardId: string): IntroRequestResponse | null {
  const submitted = sessionStorage.getItem(INTRO_REQUEST_KEYS.SUBMITTED);
  const submittedAt = sessionStorage.getItem(INTRO_REQUEST_KEYS.SUBMITTED_AT);

  if (submitted === cardId && submittedAt) {
    return {
      requestId: `req_existing_${cardId}`,
      status: 'pending',
      submittedAt,
      isDuplicate: true,
    };
  }

  return null;
}

/**
 * Recipient-side intro request API.
 *
 * G1 ACT-INDEEP-WITHOUT-FILTERS-INTRO-RECIPIENT-VERTICAL01:
 *   - listRecipientInbox
 *   - getRecipientIntroRequest
 *   - acceptIntroRequest
 *   - rejectIntroRequest
 *   - cancelIntroRequest
 *
 * Server is authoritative; the client MUST NOT make authorization
 * decisions. All four operations bind the caller's session via
 * cookie auth (credentials: 'include').
 */

/**
 * Canonical recipient-side status (4-state machine per ADR-009 D3).
 */
export type RecipientIntroStatus = 'pending' | 'accepted' | 'rejected' | 'cancelled';

export interface IntroInboxItem {
  id: string;
  message?: string;
  status: RecipientIntroStatus;
  created_at: string;
}

export interface IntroInboxResponse {
  items: IntroInboxItem[];
  total: number;
}

export interface IntroDetailResponse {
  id: string;
  message: string;
  status: RecipientIntroStatus;
  created_at: string;
  decided_at?: string;
}

/**
 * List the recipient's incoming intro-request inbox.
 * GET /api/v1/intro-requests
 */
export async function listRecipientInbox(
  limit: number = 50,
  offset: number = 0
): Promise<IntroInboxResponse> {
  const response = await fetch(
    `${API_BASE}/intro-requests?limit=${limit}&offset=${offset}`,
    {
      method: 'GET',
      credentials: 'include',
    }
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch inbox: ${response.status}`);
  }
  return await response.json();
}

/**
 * Get a single intro request by id (recipient get-one).
 * GET /api/v1/intro-requests/{id}
 *
 * Returns null when the row is missing OR the caller is not the
 * canonical recipient (the server returns 404 for both cases).
 */
export async function getRecipientIntroRequest(
  id: string
): Promise<IntroDetailResponse | null> {
  const response = await fetch(`${API_BASE}/intro-requests/${id}`, {
    method: 'GET',
    credentials: 'include',
  });
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch intro request: ${response.status}`);
  }
  return await response.json();
}

/**
 * Accept an intro request (recipient).
 * POST /api/v1/intro-requests/{id}/accept
 */
export async function acceptIntroRequest(
  id: string
): Promise<IntroDetailResponse> {
  return await postTransition(`${API_BASE}/intro-requests/${id}/accept`, id);
}

/**
 * Reject an intro request (recipient).
 * POST /api/v1/intro-requests/{id}/reject
 */
export async function rejectIntroRequest(
  id: string
): Promise<IntroDetailResponse> {
  return await postTransition(`${API_BASE}/intro-requests/${id}/reject`, id);
}

/**
 * Cancel an intro request (sender).
 * POST /api/v1/intro-requests/{id}/cancel
 */
export async function cancelIntroRequest(
  id: string
): Promise<IntroDetailResponse> {
  return await postTransition(`${API_BASE}/intro-requests/${id}/cancel`, id);
}

/**
 * postTransition is the shared implementation for the state-changing
 * intro-request endpoints. CSRF is enforced server-side via the
 * existing cookie-auth + double-submit middleware; we add the
 * X-CSRF-Token header when a token is available in sessionStorage.
 */
async function postTransition(url: string, _id: string): Promise<IntroDetailResponse> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const csrfToken = sessionStorage.getItem('csrf_token');
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({}),
  });
  if (!response.ok) {
    let message = `Transition failed: ${response.status}`;
    try {
      const data = await response.json();
      if (data.error) message = data.error;
    } catch {
      // ignore JSON parse errors
    }
    throw new Error(message);
  }
  return await response.json();
}
